import {
  convertEffort,
  effortLabel,
  listDefaultWeightCandidates,
  type EffortScale,
  type LastLoggedSet,
  type MaxForProposal,
  type WeightSource,
} from '@heraion/shared';

import { weightProposalTip } from './weightProposalCue';

// LG-59 §12.2 — the history widget's FIRST section: every weight the app can
// work out for the set in hand, the one actually in the cell on top and
// marked as such.
//
// ⭐ Antenore, 2026-09-02: «l'informazione da dove viene il 50 KG del front
// squat va nel widget history». It used to be a glyph and a bubble beside the
// KG label; a label that says the unit should say the unit, and the
// provenance belongs where the athlete goes to look things up.
//
// ⛔ This module does NOT compute a second chain. The other instruments come
// from `listDefaultWeightCandidates`, the same ordered list whose first
// element IS the weight the logger prefilled — so the panel can never name an
// instrument the cell disagrees with. What this adds is the reading: which
// one is in use, and how each one is worded.
//
// ⛔ And the row IN USE is read off the set's own persisted `weightSource`,
// never recomputed. A percent-of-MAX or an absolute prescription was resolved
// when the workout started, possibly against a MAX that has since moved:
// re-deriving it here would quietly show a number the athlete never got.

export type WeightCandidateRow = {
  /** Canonical kg. The caller formats it in the athlete's unit. */
  kg: number;
  source: WeightSource;
  /** True for the value the cell is actually holding. First in the list. */
  inUse: boolean;
  /** The compact provenance line («RIR 5 dal tuo MAX»). */
  label: string;
  /** The long form, for a screen reader. Absent when there isn't one. */
  hint?: string;
};

const sourceLabel = (
  source: WeightSource,
  effort: string | undefined,
  pctOfMax: number | undefined,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string => {
  // ⛔ Every branch is a whole key, never a sentence assembled from fragments:
  // the five locales put the effort in different places, and a "{{effort}} da
  // {{source}}" template would read as a machine in at least two of them.
  //
  // ⭐ And each line has to stand on its own — Antenore, 2026-09-02: «se la
  // linea non è chiara, l'utente non sa cosa prendere». So the phrase names
  // BOTH what the weight is for and where it came from, instead of a bare
  // source tag whose meaning the athlete would have to already know.
  if (source === 'effort-from-tested-max') {
    return t('mobile.logger.historySourceTestedMax', {
      effort,
      defaultValue: 'To land on {{effort}} — from the 1RM you tested',
    });
  }
  if (source === 'effort-from-max') {
    return t('mobile.logger.historySourceMax', {
      effort,
      defaultValue: 'To land on {{effort}} — from your recorded max',
    });
  }
  if (source === 'effort-from-history') {
    return t('mobile.logger.historySourceLastSet', {
      effort,
      defaultValue: 'To land on {{effort}} — from your last set here',
    });
  }
  if (source === 'computed-percent') {
    // ⛔ Names the percentage when the prescription still has it. «A
    // percentage of your max» is a CATEGORY, not an explanation — it was the
    // one line of the five that left the athlete to guess why that kg is
    // there (a11y audit, 2026-09-02). The vague form survives only for a set
    // whose intent no longer carries the number.
    return pctOfMax === undefined
      ? t('mobile.logger.historySourcePercent', {
          defaultValue: 'A percentage of your max, set by the program',
        })
      : t('mobile.logger.historySourcePercentOf', {
          pct: pctOfMax,
          defaultValue: '{{pct}}% of your max, set by the program',
        });
  }
  if (source === 'prescribed-absolute') {
    return t('mobile.logger.historySourcePrescribed', {
      defaultValue: 'The weight written in the program',
    });
  }
  if (source === 'history') {
    return t('mobile.logger.historySourceLastWeight', {
      defaultValue: 'The last weight you actually used here',
    });
  }
  // 'manual' — the athlete typed it. Nothing was worked out, and saying so is
  // the honest answer: this row exists so the section is never mutely empty
  // when they have overwritten the proposal.
  return t('mobile.logger.historySourceManual', {
    defaultValue: 'You typed this one yourself',
  });
};

export function buildWeightCandidateRows(opts: {
  /** The set in hand: what the cell holds and what it was asked to reach. */
  set: {
    weightKg: number | null;
    weightSource: WeightSource | undefined;
    rirPrescribed: number | undefined;
    reps: number | undefined;
    /** The percentage the program wrote, when the axis IS a percentage. */
    pctOfMax?: number;
  };
  /** The athlete's MAX for this exercise, when one is known. ⛔ `source` and
   *  `setAt` optional and NOT defaulted: absent means unknown, which the chain
   *  reads as untrusted and as stale respectively — the cautious direction
   *  `isMaxTrustedForProposal` / `isMaxStaleForProposal` already document. */
  max: MaxForProposal | undefined;
  lastLogged: LastLoggedSet | undefined;
  effortScale: EffortScale;
  t: (k: string, opts?: Record<string, unknown>) => string;
}): WeightCandidateRow[] {
  const { set, max, lastLogged, effortScale, t } = opts;

  const effort =
    set.rirPrescribed === undefined
      ? undefined
      : effortLabel(
          convertEffort(set.rirPrescribed, 'rir', effortScale),
          effortScale,
        );

  const rowFor = (
    kg: number,
    source: WeightSource,
    inUse: boolean,
  ): WeightCandidateRow => ({
    kg,
    source,
    inUse,
    label: sourceLabel(source, effort, set.pctOfMax, t),
    hint: weightProposalTip({
      weightSource: source,
      rirPrescribed: set.rirPrescribed,
      effortScale,
      t,
    }),
  });

  const rows: WeightCandidateRow[] = [];
  if (set.weightKg !== null && set.weightSource !== undefined) {
    rows.push(rowFor(set.weightKg, set.weightSource, true));
  }

  // The alternatives only exist where an effort target names them: without
  // "N reps at RIR r" there is no question for a second instrument to answer,
  // and listing "the last weight you used" beside a prescribed 82.5 kg would
  // dress history up as an option the program did not offer.
  const reps = set.reps;
  if (
    set.rirPrescribed !== undefined &&
    reps !== undefined &&
    Number.isFinite(reps) &&
    reps > 0
  ) {
    const others = listDefaultWeightCandidates(
      { kind: 'none' },
      {
        userMax: max?.valueKg,
        userMaxSource: max?.source,
        userMaxSetAt: max?.setAt,
        lastLogged,
        effort: { targetRir: set.rirPrescribed, reps },
      },
    );
    for (const c of others) {
      if (rows.some((r) => r.source === c.source)) continue;
      rows.push(rowFor(c.kg, c.source, false));
    }
  }
  return rows;
}
